import React from 'react';
import { Layers, ShieldCheck, Workflow, Building2, Compass } from 'lucide-react';

const About = () => {
  const highlights = [
    {
      icon: <Layers className="text-brand-primary" size={24} />,
      title: "20+ Years' Experience",
      description: "Product, project, R&D and operational transformation across regulated and technology-led organisations"
    },
    {
      icon: <Building2 className="text-brand-primary" size={24} />,
      title: "Enterprise Implementation",
      description: "Replacing legacy systems with modern digital platforms, from requirements to UAT and adoption"
    },
    {
      icon: <ShieldCheck className="text-brand-primary" size={24} />,
      title: "Governance & Security",
      description: "GDPR, privacy operations, OneTrust and ISO 27001-aligned controls embedded into change"
    },
    {
      icon: <Workflow className="text-brand-primary" size={24} />,
      title: "Secure Automation",
      description: "Workflow automation with Microsoft 365 and Power Automate, plus AI-assisted internal platforms"
    }
  ];

  const languages = [
    { lang: "English", level: "Fluent" },
    { lang: "Ukrainian", level: "Native" },
    { lang: "Russian", level: "Native" },
    { lang: "Arabic", level: "B1" }
  ];

  return (
    <section id="about" className="py-24 bg-bg-page" data-testid="about-section">
      <div className="container mx-auto px-8">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="heading-2 text-brand-primary mb-6">Professional Summary</h2>
            <p className="body-large max-w-3xl mx-auto">
              Product | Digital Transformation | Data Governance | Secure Automation
            </p>
          </div>

          {/* Main Content */}
          <div className="grid lg:grid-cols-2 gap-16 mb-16 items-start">
            {/* Summary */}
            <div data-testid="about-summary">
              <h3 className="heading-4 text-text-primary mb-6">Who I Am</h3>
              <div className="space-y-4">
                <p className="body-medium">
                  I am a Product, Project and Digital Transformation professional with 20+ years of experience delivering enterprise-system implementation, workflow automation, data migration, product development and governance-led change.
                </p>
                <p className="body-medium">
                  I translate complex operational and regulatory requirements into practical, secure and scalable digital solutions. My experience spans technical and business requirements, UAT, stakeholder management, process improvement, GDPR, ISO 27001-aligned controls, Microsoft 365 and Power Automate.
                </p>
                <p className="body-medium">
                  I work at the intersection of product delivery, data governance, information security and operational adoption — helping organisations improve service quality, reduce manual effort and build trust in their systems and data.
                </p>
              </div>
            </div>

            {/* Image */}
            <div className="relative">
              <div className="bg-bg-card rounded-lg overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1602016736566-7ed6a58894bd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB3b21hbiUyMHdvcmtpbmclMjBsYXB0b3AlMjBvZmZpY2V8ZW58MHx8fHwxNzg2NDQ2MDI1fDA&ixlib=rb-4.1.0&q=85"
                  alt="Delivering enterprise digital solutions"
                  className="w-full h-96 object-cover"
                />
              </div>
            </div>
          </div>

          {/* Highlights Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {highlights.map((highlight, index) => (
              <div key={index} className="team-card p-6 text-center" data-testid={`about-highlight-${index}`}>
                <div className="flex justify-center mb-4">
                  {highlight.icon}
                </div>
                <h4 className="heading-6 text-text-primary mb-3">{highlight.title}</h4>
                <p className="body-small">{highlight.description}</p>
              </div>
            ))}
          </div>

          {/* Current Focus */}
          <div className="team-card p-8 mb-16" data-testid="about-current-focus">
            <div className="flex items-start space-x-4">
              <Compass className="text-brand-primary flex-shrink-0 mt-1" size={28} />
              <div>
                <h3 className="heading-4 text-text-primary mb-4">Current Focus</h3>
                <p className="body-medium">
                  Current focus areas include secure AI-enabled automation, data governance, enterprise transformation and product operations in regulated environments. I am particularly interested in B2B SaaS, GovTech, HealthTech, cybersecurity, compliance and workflow-automation products.
                </p>
              </div>
            </div>
          </div>

          {/* Languages & Education */}
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Languages */}
            <div>
              <h3 className="heading-4 text-text-primary mb-6">Languages</h3>
              <div className="space-y-4">
                {languages.map((item, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b border-border-light">
                    <span className="body-medium text-text-primary">{item.lang}</span>
                    <span className="caption text-text-secondary">{item.level}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Education */}
            <div>
              <h3 className="heading-4 text-text-primary mb-6">Education</h3>
              <div className="space-y-4">
                <div className="p-4 bg-bg-card rounded-lg">
                  <h4 className="body-medium text-text-primary mb-2">Master's Degree</h4>
                  <p className="body-small text-text-secondary">Comparative Political Science, Diplomatic and Consular Service</p>
                  <p className="caption text-text-muted">Kyiv International University</p>
                </div>
                <div className="p-4 bg-bg-card rounded-lg">
                  <h4 className="body-medium text-text-primary mb-2">Bachelor's Degree</h4>
                  <p className="body-small text-text-secondary">Foreign Politics</p>
                  <p className="caption text-text-muted">Kyiv International University</p>
                </div>
                <div className="p-4 bg-bg-card rounded-lg">
                  <h4 className="body-medium text-text-primary mb-2">Engineering Degree</h4>
                  <p className="body-small text-text-secondary">Mechanical Engineer</p>
                  <p className="caption text-text-muted">National Technical University of Ukraine 'Kyiv Polytechnic Institute'</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
